import * as React from 'react';
import { createPortal } from 'react-dom';
import {
  init,
  getInstanceByDom,
  type ECharts,
  type EChartsOption,
  type TooltipComponentPositionCallbackParams,
} from 'echarts';
import { throttle, mergeWith } from 'lodash';
import { useResizeObserver } from '@shared/hooks';
import { customizerMerge, flushBrush } from './utils';
import { DEFAULT_OPTIONS } from './options';

type NarrowArrayItem<T> = T extends (infer U)[] ? U : never;
export type CustomTooltipParam = NarrowArrayItem<TooltipComponentPositionCallbackParams>;

export interface BaseChartRef {
  getEChartsElement: () => HTMLDivElement | null;
  getEChartsInstance: () => ECharts | undefined;
}

export interface BaseChartProps {
  defaultRenderer?: 'canvas' | 'svg';
  options: EChartsOption | ((instance: ECharts | undefined) => EChartsOption);
  customTooltip?: (p: CustomTooltipParam[], op: EChartsOption) => React.ReactNode;
  fit?: 'container' | [number, number];
  events?: {
    [key: string]: {
      handler: (...args: any[]) => void;
      query?: string | Record<string, unknown>;
      context?: unknown;
    };
  };
  className?: string;
  style?: React.CSSProperties;
}

export const BaseChart = React.forwardRef<BaseChartRef, BaseChartProps>((props, ref) => {
  const {
    defaultRenderer = 'svg',
    options,
    events,
    fit = 'container',
    customTooltip,
    className,
    style,
  } = props;

  const containerElRef = React.useRef<HTMLDivElement>(null);
  const echartsInstanceRef = React.useRef<ECharts>();
  const tooltipRootRef = React.useRef(document.createElement('div'));
  const [tooltipParams, setTooltipParams] = React.useState<CustomTooltipParam[]>();

  const getAliveChart = React.useCallback(() => {
    const ins = echartsInstanceRef.current;
    if (!ins || ins.isDisposed()) return undefined;
    return ins;
  }, []);

  const tooltipFormatterRef = React.useRef<ReturnType<typeof throttle>>();
  const onResizeRef = React.useRef<ReturnType<typeof throttle>>();
  const rafIdRef = React.useRef<number | null>(null);

  const makeTooltipFormatter = React.useCallback(() => {
    const fn = throttle((p: TooltipComponentPositionCallbackParams) => {
      if (!getAliveChart()) return tooltipRootRef.current;
      setTooltipParams(Array.isArray(p) ? p : [p]);

      return tooltipRootRef.current;
    }, 100);
    tooltipFormatterRef.current = fn;
    return fn;
  }, [getAliveChart]);

  const initChart = React.useCallback(() => {
    const el = containerElRef.current;
    if (!el) return;

    let chart = getInstanceByDom(el);
    if (!chart || chart.isDisposed()) {
      chart = init(el, undefined, { renderer: defaultRenderer });
    }
    chart.setOption(DEFAULT_OPTIONS, { lazyUpdate: true });
    echartsInstanceRef.current = chart;
  }, [defaultRenderer]);

  React.useLayoutEffect(() => {
    initChart();
    return () => {
      tooltipFormatterRef.current?.cancel?.();
      onResizeRef.current?.cancel?.();
      if (rafIdRef.current !== null) {
        cancelAnimationFrame(rafIdRef.current);
        rafIdRef.current = null;
      }

      const chart = echartsInstanceRef.current;
      echartsInstanceRef.current = undefined;
      if (chart && !chart.isDisposed()) chart.dispose();
    };
  }, [initChart]);

  React.useEffect(() => {
    const chart = getAliveChart();
    if (!chart || !events) return;

    for (const [name, content] of Object.entries(events)) {
      chart.off(name, content.handler);
      if (content.query) {
        chart.on(name, content.query, content.handler, content.context);
      } else {
        chart.on(name, content.handler, content.context);
      }
    }

    return () => {
      const alive = getAliveChart();
      if (!alive || !events) return;
      for (const [name, content] of Object.entries(events)) {
        alive.off(name, content.handler);
      }
    };
  }, [events, getAliveChart]);

  const updateOptions = React.useCallback(() => {
    const chart = getAliveChart();
    if (!chart) return;

    const customOptions = typeof options === 'function' ? options(chart) : options;
    const nextOptions = mergeWith({}, DEFAULT_OPTIONS, customOptions, customizerMerge);

    chart.setOption(nextOptions, { notMerge: true, lazyUpdate: true });

    flushBrush(chart, nextOptions);

    chart.setOption(
      {
        tooltip: customTooltip
          ? {
              ...customOptions.tooltip,
              padding: 0,
              borderWidth: 0,
              appendToBody: true,
              enterable: true,
              // Don't let tooltip fly around, it's too hard to catch
              position: (point, params, dom, rect, size) => {
                const xPos = point[0] + size.contentSize[0];
                const yPos = point[1] + size.contentSize[1];
                let x = point[0] + 5;
                let y = point[1] + 5;

                if (xPos > window.innerWidth || xPos > size.viewSize[0]) {
                  x = point[0] - size.contentSize[0] - 5;
                }
                if (yPos > window.innerHeight || yPos > size.viewSize[1]) {
                  y = point[1] - size.contentSize[1] - 5;
                }
                return [x, y];
              },

              formatter: makeTooltipFormatter(),
            }
          : customOptions.tooltip,
      },
      { replaceMerge: 'tooltip', lazyUpdate: true },
    );
  }, [options, customTooltip, makeTooltipFormatter, getAliveChart]);

  React.useEffect(() => {
    updateOptions();
  }, [updateOptions]);

  React.useImperativeHandle(
    ref,
    () => ({
      getEChartsElement: () => containerElRef.current,
      getEChartsInstance: () => getAliveChart(),
    }),
    [getAliveChart],
  );

  const onResize = React.useMemo(() => {
    const fn = throttle(
      () => {
        const el = containerElRef.current;
        const rect = el?.getBoundingClientRect();
        if (!rect || rect.width * rect.height === 0) return;

        const chart = getAliveChart();
        if (!chart) return;

        updateOptions();
        chart.resize();
      },
      100,
      {
        leading: true,
        trailing: true,
      },
    );

    onResizeRef.current = fn;
    return fn;
  }, [getAliveChart, updateOptions]);

  useResizeObserver({
    ref: containerElRef,
    onResize: () => {
      if (rafIdRef.current !== null) cancelAnimationFrame(rafIdRef.current);
      rafIdRef.current = requestAnimationFrame(onResize);
    },
  });

  const finallyStyle = React.useMemo<React.CSSProperties>(
    () =>
      fit === 'container'
        ? { ...style, width: 'calc(100% - 1px)', height: 'calc(100% - 1px)' }
        : { ...style, aspectRatio: `${fit[0]} / ${fit[1]}` },
    [fit, style],
  );

  return (
    <>
      <div ref={containerElRef} className={className} style={finallyStyle} />
      {!!customTooltip &&
        !!tooltipParams &&
        createPortal(
          customTooltip(
            tooltipParams,
            typeof options === 'function' ? options(getAliveChart()) : options,
          ),
          tooltipRootRef.current,
        )}
    </>
  );
});

BaseChart.displayName = 'BaseChart';
